<script setup lang="ts" generic="T extends CarouselItem">
import type { CarouselItem, CarouselProps } from '#ui/components/Carousel.vue'
import type { ViewportType } from '~/types/elementsUi'
import { useViewport } from '#imports'
import { nuxtUiMerge } from '~/utils/tw-utils'
import type { AutoplayOptionsType } from 'embla-carousel-autoplay'

type WidthOptions = 1 | 2 | 3 | 4 | 'auto'

interface VCarouselProps {
  items: T[]
  srLabel: string
  numberOfItems?: WidthOptions | Partial<Record<ViewportType, WidthOptions>>
  loop?: boolean
  dots?: boolean
  autoplay?: boolean | AutoplayOptionsType
  ui?: CarouselProps['ui']
}

const props = defineProps<VCarouselProps>()

const viewport = useViewport()
const { t, te } = useI18n()

const carousel = useTemplateRef('carousel')

const canScroll = ref(false)

const computedUI = computed<CarouselProps['ui']>(() => {
  const itemCount = props.items.length
  let rootMarginBottom
  if (itemCount >= 15) {
    rootMarginBottom = tw`mb-24! lg:mb-10!`
  } else {
    rootMarginBottom = tw`mb-10!`
  }

  return nuxtUiMerge(props.ui, {
    root: tw`${rootMarginBottom} pb-4`,
    viewport: tw`pb-4`,
    item: tw`${itemWidthClass.value ?? ''} ps-(--column-margin)`,
    container: tw`items-stretch`,
    dots: tw`-top-7`,
    dot: tw`data-[state=active]:bg-primary dark:data-[state=active]:bg-success border-solid [&:not([data-state=active])]:border border-gray-200 dark:border-gray-700`,
    controls: tw`absolute -bottom-1 inset-x-12`
  })
})

const itemWidthClassMapping: Record<ViewportType, Record<WidthOptions, string>> = {
  mobile: {
    '1': tw`shrink-0`,
    '2': tw`basis-1/2`,
    '3': tw`basis-1/3`,
    '4': tw`basis-1/4`,
    auto: tw`shrink-1`
  },
  tablet: {
    '1': tw`md:shrink-0`,
    '2': tw`md:basis-1/2`,
    '3': tw`md:basis-1/3`,
    '4': tw`md:basis-1/4`,
    auto: tw`md:shrink-1`
  },
  desktop: {
    '1': tw`lg:shrink-0`,
    '2': tw`lg:basis-1/2`,
    '3': tw`lg:basis-1/3`,
    '4': tw`lg:basis-1/4`,
    auto: tw`lg:shrink-1`
  },
  widescreen: {
    '1': tw`xl:shrink-0`,
    '2': tw`xl:basis-1/2`,
    '3': tw`xl:basis-1/3`,
    '4': tw`xl:basis-1/4`,
    auto: tw`xl:shrink-1`
  },
  fullHD: {
    '1': tw`2xl:shrink-0`,
    '2': tw`2xl:basis-1/2`,
    '3': tw`2xl:basis-1/3`,
    '4': tw`2xl:basis-1/4`,
    auto: tw`2xl:shrink-1`
  }
}

const itemWidthClass = computed<string | undefined>(() => {
  if (!props.numberOfItems) {
    return undefined
  }

  if (typeof props.numberOfItems === 'number' || props.numberOfItems === 'auto') {
    return itemWidthClassMapping['mobile'][props.numberOfItems]
  }

  return Object.entries(props.numberOfItems)
    .map(
      ([key, value]) => itemWidthClassMapping[key as ViewportType][value as WidthOptions]
    )
    .join(' ')
})

const getNumberOfItems = (viewport: ViewportType) => {
  if (!props.numberOfItems || props.numberOfItems === 'auto') {
    return undefined
  }

  if (typeof props.numberOfItems === 'number') {
    return props.numberOfItems
  }

  const baseNumberOfItems = props.numberOfItems[viewport]
  if (baseNumberOfItems) {
    return baseNumberOfItems
  }

  if (viewport === 'mobile') {
    return undefined
  } else if (viewport === 'tablet') {
    return getNumberOfItems('mobile')
  } else if (viewport === 'desktop') {
    return getNumberOfItems('tablet')
  } else if (viewport === 'widescreen') {
    return getNumberOfItems('desktop')
  } else if (viewport === 'fullHD') {
    return getNumberOfItems('widescreen')
  } else {
    return undefined
  }
}

const setSlidesToScroll = (value: ViewportType) => {
  const slidesToScroll = getNumberOfItems(value)
  if (!carousel.value?.emblaApi || !slidesToScroll) {
    return
  }

  canScroll.value = slidesToScroll === 'auto' || slidesToScroll < props.items.length

  carousel.value.emblaApi.reInit({
    slidesToScroll
  })
}

watch(() => viewport.current.value, setSlidesToScroll, { immediate: true })
watch(
  () => carousel.value?.emblaApi,
  async () => {
    // Wait one tick for the carousel to be initialized, else the parameters get overwritten after we call `reInit`
    await nextTick()
    setSlidesToScroll(viewport.current.value)
  },
  { once: true }
)
onMounted(() => {
  setSlidesToScroll(viewport.current.value)
})

const isScrollable = () => canScroll.value
</script>

<template>
  <UCarousel
    ref="carousel"
    auto-height
    :items
    :loop
    :dots="dots && canScroll"
    :ui="computedUI"
    :arrows="canScroll"
    :watch-drag="isScrollable"
    wheel-gestures
    :aria-label="te(srLabel) ? t(srLabel) : srLabel"
  >
    <template #default="{ item, index }">
      <slot name="default" :item="item" :index="index" />
    </template>
  </UCarousel>
</template>

<style scoped></style>
